import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Inbox, ArrowRight, CheckCircle2, User, Mail, Phone, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const PRIO_ORDEN = { alta: 0, media: 1, baja: 2 };

const PRIO_PILL = {
    alta: "bg-destructive/15 text-destructive border-destructive/40",
    media: "bg-warning/20 text-warning border-warning/40",
    baja: "bg-muted text-muted-foreground border-border",
};

const PRIO_LABEL = { alta: "Alta", media: "Media", baja: "Baja" };

const CAT_LABEL = {
    limpieza: "Limpieza",
    mantenimiento: "Mantenimiento",
    app_foresta: "App Foresta",
    internet: "Internet",
    climatizacion: "Climatización",
    electricidad: "Electricidad",
    otros: "Otros",
    // Legacy — incidencias antiguas
    seguridad: "Seguridad",
};

// Cada categoría tiene su propio acento (sutil pero distinto)
const CAT_PILL = {
    internet: "bg-sky-500/15 text-sky-700 border-sky-500/30",
    limpieza: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
    mantenimiento: "bg-amber-500/15 text-amber-700 border-amber-500/30",
    app_foresta: "bg-indigo-500/15 text-indigo-700 border-indigo-500/30",
    climatizacion: "bg-cyan-500/15 text-cyan-700 border-cyan-500/30",
    electricidad: "bg-yellow-500/20 text-yellow-800 border-yellow-500/40",
    otros: "bg-muted text-muted-foreground border-border",
    seguridad: "bg-rose-500/15 text-rose-700 border-rose-500/30",
};

function fmtCuando(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const ayer = new Date(today); ayer.setDate(ayer.getDate() - 1);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    if (sameDay(d, today)) return `Hoy ${hh}:${mm}`;
    if (sameDay(d, ayer)) return `Ayer ${hh}:${mm}`;
    const dd = String(d.getDate()).padStart(2, "0");
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mo} ${hh}:${mm}`;
}

export default function IncidenciasKanban() {
    const [items, setItems] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [tab, setTab] = useState("activas");
    const [busy, setBusy] = useState(null);
    const [detalle, setDetalle] = useState(null);

    const load = () => api.get("/incidencias").then((r) => setItems(r.data));
    useEffect(() => {
        api.get("/unidades").then((r) => setUnidades(r.data));
        load();
    }, []);

    const uniName = (item) => item?.unidad_etiqueta
        || unidades.find((u) => u.unidad_id === item?.unidad_id)?.etiqueta
        || "—";

    const recibidos = useMemo(
        () => items.filter((i) => i.estado === "recibido")
            .sort((a, b) => a.creado_en.localeCompare(b.creado_en)),
        [items]
    );
    const enProceso = useMemo(
        () => items.filter((i) => i.estado === "en_proceso").sort((a, b) => {
            const pa = PRIO_ORDEN[a.prioridad] ?? 99;
            const pb = PRIO_ORDEN[b.prioridad] ?? 99;
            if (pa !== pb) return pa - pb;
            return a.creado_en.localeCompare(b.creado_en);
        }),
        [items]
    );
    const resueltos = useMemo(
        () => items.filter((i) => i.estado === "resuelto").sort((a, b) =>
            (b.fecha_resolucion || b.actualizado_en).localeCompare(a.fecha_resolucion || a.actualizado_en)
        ),
        [items]
    );

    const asignarPrioridad = async (id, prioridad) => {
        setBusy(id);
        // optimistic
        setItems((arr) => arr.map((x) => x.incidencia_id === id ? { ...x, prioridad } : x));
        try {
            await api.patch(`/incidencias/${id}/prioridad`, { prioridad });
        } catch (e) { toast.error(formatApiError(e)); load(); }
        finally { setBusy(null); }
    };

    const cambiarEstado = async (id, estado) => {
        setBusy(id);
        try {
            const r = await api.patch(`/incidencias/${id}/estado`, { estado });
            setItems((arr) => arr.map((x) => x.incidencia_id === id ? r.data : x));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const totalActivas = recibidos.length + enProceso.length;

    return (
        <div>
            {/* Header compacto en una sola línea */}
            <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <h1 className="font-heading font-extrabold tracking-tight text-2xl leading-none">Incidencias</h1>
                    <p className="text-xs text-muted-foreground mt-1">Priorizá y resolvé reclamos.</p>
                </div>
                <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
                    <button
                        type="button"
                        onClick={() => setTab("activas")}
                        data-testid="tab-activas"
                        className={`px-3 text-xs font-medium transition-colors ${tab === "activas" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                    >
                        Activas <span className="ml-1 text-[10px] opacity-80">{totalActivas}</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab("resueltas")}
                        data-testid="tab-resueltas"
                        className={`px-3 text-xs font-medium transition-colors border-l border-border ${tab === "resueltas" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-primary-soft"}`}
                    >
                        Resueltas <span className="ml-1 text-[10px] opacity-80">{resueltos.length}</span>
                    </button>
                </div>
            </div>

            {tab === "activas" ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    <Column
                        title="Recibido"
                        icon={Inbox}
                        items={recibidos}
                        empty="No hay reclamos nuevos."
                        renderItem={(i) => (
                            <CardRecibido
                                key={i.incidencia_id}
                                item={i}
                                uniName={uniName(i)}
                                busy={busy === i.incidencia_id}
                                onOpen={() => setDetalle(i)}
                                onPrioridad={(p) => asignarPrioridad(i.incidencia_id, p)}
                                onProceso={() => cambiarEstado(i.incidencia_id, "en_proceso")}
                            />
                        )}
                    />
                    <Column
                        title="En proceso"
                        icon={ArrowRight}
                        items={enProceso}
                        empty="No hay reclamos en curso."
                        renderItem={(i) => (
                            <CardProceso
                                key={i.incidencia_id}
                                item={i}
                                uniName={uniName(i)}
                                busy={busy === i.incidencia_id}
                                onOpen={() => setDetalle(i)}
                                onResolver={() => cambiarEstado(i.incidencia_id, "resuelto")}
                            />
                        )}
                    />
                </div>
            ) : (
                <Card className="overflow-hidden">
                    <CardContent className="p-0">
                        {resueltos.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-8 text-center">Todavía no hay reclamos resueltos.</p>
                        ) : (
                            <table className="w-full text-[13px]">
                                <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="text-left font-semibold px-3 py-1.5 w-[120px]">Categoría</th>
                                        <th className="text-left font-semibold px-3 py-1.5">Título</th>
                                        <th className="text-left font-semibold px-3 py-1.5 w-[100px]">Unidad</th>
                                        <th className="text-left font-semibold px-3 py-1.5 w-[140px]">Solicitante</th>
                                        <th className="text-left font-semibold px-3 py-1.5 w-[90px]">Prioridad</th>
                                        <th className="text-left font-semibold px-3 py-1.5 w-[120px]">Creado</th>
                                        <th className="text-left font-semibold px-3 py-1.5 w-[120px]">Resuelto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {resueltos.map((r) => (
                                        <tr key={r.incidencia_id} onClick={() => setDetalle(r)} className="border-t border-border hover:bg-primary-soft/30 cursor-pointer">
                                            <td className="px-3 py-1.5"><CategoriaPill cat={r.categoria} /></td>
                                            <td className="px-3 py-1.5"><span className="truncate block max-w-[28ch]">{r.titulo}</span></td>
                                            <td className="px-3 py-1.5 text-muted-foreground font-mono">{uniName(r)}</td>
                                            <td className="px-3 py-1.5 text-muted-foreground truncate">{r.creado_por_nombre || <span className="italic">Sin identificar</span>}</td>
                                            <td className="px-3 py-1.5"><PrioPill prioridad={r.prioridad} /></td>
                                            <td className="px-3 py-1.5 text-muted-foreground text-xs font-mono">{fmtCuando(r.creado_en)}</td>
                                            <td className="px-3 py-1.5 text-muted-foreground text-xs font-mono">{fmtCuando(r.fecha_resolucion || r.actualizado_en)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>
            )}

            <DetalleModal incidencia={detalle} unidadEtiqueta={detalle ? uniName(detalle) : ""} onClose={() => setDetalle(null)} />
        </div>
    );
}

function DetalleModal({ incidencia, unidadEtiqueta, onClose }) {
    if (!incidencia) return null;
    const wa = (incidencia.creado_por_whatsapp || "").trim();
    const waClean = wa.replace(/[^\d+]/g, "");
    const waLink = waClean ? `https://wa.me/${waClean.replace(/^\+/, "")}` : null;
    const copy = async (text, label) => {
        if (!text) return;
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copiado`); }
        catch { toast.error("No se pudo copiar"); }
    };

    return (
        <Dialog open={!!incidencia} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CategoriaPill cat={incidencia.categoria} />
                        <span className="truncate">{incidencia.titulo}</span>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                    <div className="flex gap-2 items-center">
                        <PrioPill prioridad={incidencia.prioridad} />
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                            {incidencia.estado === "recibido" ? "Recibido" : incidencia.estado === "en_proceso" ? "En proceso" : "Resuelto"}
                        </span>
                        <span className="text-[11px] text-muted-foreground font-mono ml-auto">{fmtCuando(incidencia.creado_en)}</span>
                    </div>

                    {incidencia.descripcion && (
                        <p className="text-sm bg-muted/30 rounded-md px-3 py-2 leading-relaxed">{incidencia.descripcion}</p>
                    )}

                    <div className="space-y-1.5 border-t border-border pt-3">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Solicitante</p>
                        {incidencia.creado_por_nombre ? (
                            <>
                                <div className="flex items-center gap-2 text-sm">
                                    <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                    <span className="font-medium">{incidencia.creado_por_nombre}</span>
                                    <span className="font-mono text-[11px] text-muted-foreground ml-auto">{unidadEtiqueta}</span>
                                </div>
                                {incidencia.creado_por_email && (
                                    <div className="flex items-center gap-2 text-[12px]">
                                        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                        <a href={`mailto:${incidencia.creado_por_email}`} className="text-primary hover:underline truncate flex-1">{incidencia.creado_por_email}</a>
                                        <button onClick={() => copy(incidencia.creado_por_email, "Email")} className="text-muted-foreground hover:text-foreground p-1 shrink-0" title="Copiar email" data-testid="copy-email">
                                            <Copy className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                                {wa && (
                                    <div className="flex items-center gap-2 text-[12px]">
                                        <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                        <span className="font-mono flex-1 truncate">{wa}</span>
                                        {waLink && (
                                            <a
                                                href={waLink}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-success hover:opacity-80 p-1 shrink-0"
                                                title="Abrir WhatsApp"
                                                data-testid="open-whatsapp"
                                            >
                                                <MessageCircle className="h-3 w-3" />
                                            </a>
                                        )}
                                        <button onClick={() => copy(wa, "WhatsApp")} className="text-muted-foreground hover:text-foreground p-1 shrink-0" title="Copiar" data-testid="copy-whatsapp">
                                            <Copy className="h-3 w-3" />
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="text-[12px] text-muted-foreground italic">Solicitante no identificado.</p>
                        )}
                    </div>

                    {incidencia.foto_urls?.length > 0 && (
                        <div className="space-y-1.5 border-t border-border pt-3">
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Fotos</p>
                            <div className="flex flex-wrap gap-2">
                                {incidencia.foto_urls.map((url, idx) => (
                                    <a key={idx} href={url} target="_blank" rel="noreferrer">
                                        <img src={url} alt={`Foto ${idx + 1}`} className="h-20 w-20 object-cover rounded-md border border-border" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button onClick={onClose}>Cerrar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Column({ title, icon: Icon, items, empty, renderItem }) {
    return (
        <div className="rounded-md border border-border bg-muted/20">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                <h3 className="font-heading font-bold text-xs uppercase tracking-wider">{title}</h3>
                <span className="text-[11px] text-muted-foreground font-mono">· {items.length}</span>
            </div>
            <div className="p-2 space-y-1.5 max-h-[calc(100vh-220px)] overflow-y-auto">
                {items.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground text-center py-3">{empty}</p>
                ) : items.map(renderItem)}
            </div>
        </div>
    );
}

function CategoriaPill({ cat }) {
    const cls = CAT_PILL[cat] || CAT_PILL.otros;
    return (
        <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${cls}`}>
            {CAT_LABEL[cat] || cat}
        </span>
    );
}

function PrioPill({ prioridad }) {
    if (!prioridad) return <span className="text-[10px] text-muted-foreground italic">Sin prioridad</span>;
    return (
        <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${PRIO_PILL[prioridad]}`}>
            {PRIO_LABEL[prioridad]}
        </span>
    );
}

function PrioSelector({ value, onChange, disabled, itemId }) {
    return (
        <div className="inline-flex rounded-md overflow-hidden border border-border">
            {["alta", "media", "baja"].map((p, idx) => {
                const active = value === p;
                return (
                    <button
                        key={p}
                        type="button"
                        disabled={disabled}
                        onClick={() => onChange(p)}
                        data-testid={`prio-${p}-${itemId}`}
                        className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${idx > 0 ? "border-l border-border" : ""} ${active ? PRIO_PILL[p] : "bg-background text-muted-foreground hover:bg-muted/50"}`}
                    >
                        {PRIO_LABEL[p]}
                    </button>
                );
            })}
        </div>
    );
}

function CardRecibido({ item, uniName, busy, onPrioridad, onProceso, onOpen }) {
    const tienePrio = !!item.prioridad;
    return (
        <Card data-testid={`inc-${item.incidencia_id}`} className="bg-background shadow-none cursor-pointer hover:bg-primary-soft/20 transition-colors" onClick={onOpen}>
            <CardContent className="p-2.5 space-y-1.5">
                <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <CategoriaPill cat={item.categoria} />
                        <p className="text-sm font-semibold leading-tight truncate">{item.titulo}</p>
                    </div>
                    <PrioPill prioridad={item.prioridad} />
                </div>
                <p className="text-[11px] text-muted-foreground font-mono truncate">
                    <span className="text-foreground/80">{uniName}</span>
                    {item.creado_por_nombre ? <> · <span className="font-sans text-foreground/70">{item.creado_por_nombre}</span></> : <> · <span className="italic">Sin identificar</span></>}
                    {" · "}{fmtCuando(item.creado_en)}
                </p>
                {item.descripcion && (
                    <p className="text-xs text-muted-foreground line-clamp-2 leading-snug">{item.descripcion}</p>
                )}
                <div className="flex items-center justify-between gap-2 pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <PrioSelector
                        value={item.prioridad}
                        disabled={busy}
                        itemId={item.incidencia_id}
                        onChange={(p) => onPrioridad(p)}
                    />
                    <Button
                        size="sm"
                        variant={tienePrio ? "default" : "ghost"}
                        className="h-6 text-[11px] px-2"
                        onClick={onProceso}
                        disabled={!tienePrio || busy}
                        data-testid={`proceso-${item.incidencia_id}`}
                    >
                        {tienePrio ? (<>Pasar a proceso <ArrowRight className="h-3 w-3 ml-1" /></>) : "Asigná prioridad"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

function CardProceso({ item, uniName, busy, onResolver, onOpen }) {
    return (
        <Card data-testid={`inc-${item.incidencia_id}`} className="bg-background shadow-none cursor-pointer hover:bg-primary-soft/20 transition-colors" onClick={onOpen}>
            <CardContent className="p-2.5 space-y-1.5">
                <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <CategoriaPill cat={item.categoria} />
                        <p className="text-sm font-semibold leading-tight truncate">{item.titulo}</p>
                    </div>
                    <PrioPill prioridad={item.prioridad} />
                </div>
                <p className="text-[11px] text-muted-foreground font-mono truncate">
                    <span className="text-foreground/80">{uniName}</span>
                    {item.creado_por_nombre ? <> · <span className="font-sans text-foreground/70">{item.creado_por_nombre}</span></> : <> · <span className="italic">Sin identificar</span></>}
                    {" · "}{fmtCuando(item.creado_en)}
                </p>
                <div className="flex items-end justify-between gap-2">
                    {item.descripcion ? (
                        <p className="text-xs text-muted-foreground line-clamp-2 leading-snug flex-1">{item.descripcion}</p>
                    ) : <span className="flex-1" />}
                    <Button
                        size="sm"
                        className="h-6 text-[11px] px-2 bg-success hover:bg-success/90 text-white shrink-0"
                        onClick={(e) => { e.stopPropagation(); onResolver(); }}
                        disabled={busy}
                        data-testid={`resolver-${item.incidencia_id}`}
                    >
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Resolver
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
